import { message, Spin } from 'antd';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { deleteUser, updateUser, useGetUser } from '../../api/users';
import DangerButton from '../../shared/components/buttons/DangerButton';
import PrimaryButton from '../../shared/components/buttons/PrimaryButton';
import CancelIcon from '../../shared/svgs/CancelIcon';
import DeleteForeverIcon from '../../shared/svgs/DeleteForeverIcon';
import EditIcon from '../../shared/svgs/EditIcon';
import PersonIcon from '../../shared/svgs/PersonIcon';
import { getUsername, handleAccessControl } from '../../utils/global';
import { CurrentUserI } from '../../interfaces/auth';
import BackButton from '../../shared/components/buttons/BackButton';
import { DATE_FORMAT_FRONTEND } from '../../shared/constants';
import dayjs from 'dayjs';

const UserDetailsPage: React.FC = () => {
  const currentUser: CurrentUserI = useOutletContext();
  const access = handleAccessControl(currentUser?.roles || [], 'users');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { data: user, isLoading } = useGetUser(Number(id));

  const { mutateAsync: mutationDelete } = useMutation(deleteUser, {
    onSuccess: () => {
      message.info('Deleted!');
      queryClient.invalidateQueries({
        queryKey: ['users', 'list'],
      });
      navigate(`/users`, { replace: true });
    },
    onError: () => {
      message.error('The action is failed! Please try again');
    },
  });

  const handleDelete = () => {
    mutationDelete(Number(id));
  };

  const { mutateAsync: updateMutate } = useMutation(updateUser, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['users', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: [`users/${id}`, undefined],
      });
      message.info('Unblocked this user!');
    },
    onError: () => {
      message.error('The action is failed! Please try again');
    },
  });

  const handleUnblockUser = async () => {
    const newValues: any = user;
    newValues.blockedAt = 'null';
    updateMutate({ ...newValues, id });
  };

  return (
    <Spin spinning={isLoading}>
      <div id="user-details-container">
        <div className="page-container">
          <div className="page-content">
            <div className="page-content-header">
              <h2 className="title-page">User Details</h2>
            </div>
            <div className="details-container">
              <div className="item">
                <label>Email</label>
                <span className="value">{user?.email}</span>
              </div>
              <div className="item">
                <label>Name</label>
                <span className="value">
                  {user?.firstName} {user?.lastName}
                </span>
              </div>
              <div className="item">
                <label>Roles</label>
                <span className="value text-capitalize">
                  {user?.roles.map((role: any, index: number) => (
                    <span>
                      {index > 0 && ', '}
                      {role?.name}
                    </span>
                  ))}
                </span>
              </div>
              <div className="item">
                <label>Created At</label>
                <span className="value">{dayjs(user?.createdAt).format(DATE_FORMAT_FRONTEND)}</span>
              </div>
              <div className="item">
                <label>Created By</label>
                <span className="value">{user?.createdBy ? getUsername(user?.createdBy) : '-'}</span>
              </div>
              <div className="item">
                <label>Updated At</label>
                <span className="value">
                  {user?.updatedBy ? dayjs(user?.updatedAt).format(DATE_FORMAT_FRONTEND) : '-'}
                </span>
              </div>
              <div className="item">
                <label>Updated By</label>
                <span className="value">{user?.updatedBy ? getUsername(user?.updatedBy) : '-'}</span>
              </div>
              <div className="item">
                <label>Status</label>
                <span className={`value status ${user?.isActive ? 'active' : 'inactive'}`}>
                  {user?.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="page-content-footer">
              {user && user.blockedAt !== null && (
                <PrimaryButton
                  icon={<PersonIcon />}
                  label="Unblock"
                  onClick={handleUnblockUser}
                  color="secondary-color"
                />
              )}
              {access.isCanUpdate && (
                <Link to={`/users/${id}/edit`}>
                  <PrimaryButton label="Edit" icon={<EditIcon />} />
                </Link>
              )}
              {access.isCanDelete && <DangerButton label="Delete" onOk={handleDelete} icon={<DeleteForeverIcon />} />}
              <BackButton label="Cancel" icon={<CancelIcon />} />
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default UserDetailsPage;
